// src/modules/Courier/pages/Route/CheckPage/CheckPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useRouteStore } from '../../Route/useRouteStore';
import { useSocket }     from '../../../../context/SocketContext';
import api               from '../../../../services/api';
import styles            from '../styles/CheckPage.module.css';

export default function CheckPage() {
  const receiptId     = useRouteStore(s => s.selectedReceiptId);
  const updateReceipt = useRouteStore(s => s.updateReceipt);
  const receipt       = useRouteStore(s => s.selectedReceipt);
  const socket        = useSocket();

  const [showManualClose, setShowManualClose] = useState(false);
  const [remaining, setRemaining]             = useState(10);
  const [checkData, setCheckData]             = useState(null);
  const [status, setStatus]                   = useState('Загрузка чека…');
  const [loadingCheck, setLoadingCheck]       = useState(true);

  const countdownRef = useRef(null);

  const copyLink = link =>
    navigator.clipboard.writeText(link).then(() => alert('Ссылка скопирована'));

  const closeReceipt = async () => {
    try {
      const updated = await api.patch(
        `/receipts/${receiptId}/status`,
        { status: 'Закрыта' }
      );
      //console.log('PATCH /receipts/:id/status response:', updated);
      updateReceipt({ ...updated, is_check_issued: true });
    } catch (err) {
      console.error('Ошибка закрытия квитанции:', err);
      alert('Не удалось закрыть квитанцию');
    }
  };

  // 1) Первый GET + обратный отсчет для кнопки
  useEffect(() => {
    if (!receiptId) return;

    setShowManualClose(false);
    setRemaining(10);
    setLoadingCheck(true);

    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setShowManualClose(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    api
      .get(`/check/${receiptId}/check/details`)
      .then(payload => {
        //console.log('GET /check/details payload:', payload);
        setCheckData(payload);
        const newStatus =
          payload.status === 'completed'
            ? payload.refund_status === 'completed'
              ? 'Чек возвращён'
              : 'Чек пробит'
            : 'Ожидаем пробития…';
        setStatus(newStatus);

        if (payload.status === 'completed') {
          clearInterval(countdownRef.current);
          setShowManualClose(false);

          const toStore = {
            id:               receiptId,
            status:           'Закрыта',
            is_check_issued:  true
          };
          //console.log('→ updateReceipt from GET:', toStore);
          updateReceipt(toStore);

          api
            .patch(`/receipts/${receiptId}/status`, { status: 'Закрыта' })
            //.then(res => console.log('Background PATCH response:', res))
            .catch(console.error);
        }
      })
      .catch(() => {
        // остаёмся в ожидании WS
      })
      .finally(() => setLoadingCheck(false));

    return () => clearInterval(countdownRef.current);
  }, [receiptId, updateReceipt]);

  // 2) Подписка на WebSocket-обновления
  useEffect(() => {
    if (!socket || !receiptId) return;

    //console.log(`🌐 Subscribing WS to receiptId=${receiptId}`);
    socket.emit('check:subscribe', { receiptId }, err => {
      if (err) console.error('subscribe error', err);
    });

    const onUpdate = fullCheck => {
      //console.log('🏓 WS onUpdate fullCheck:', fullCheck);
      setCheckData(fullCheck);

      const newStatus =
        fullCheck.status === 'completed'
          ? (fullCheck.refund_status === 'completed' ? 'Чек возвращён' : 'Чек пробит')
          : 'Ожидаем пробития…';
      setStatus(newStatus);

      // При первом приходе completed и если receipt.status всё ещё "Открыта"
      if (fullCheck.status === 'completed') {
        const toStore = {
          id:               receiptId,
          status:           'Закрыта',
          is_check_issued:  true
        };
        //console.log('→ updateReceipt from WS:', toStore);
        updateReceipt(toStore);

        api
          .patch(`/receipts/${receiptId}/status`, { status: 'Закрыта' })
          //.then(res => console.log('Background PATCH response:', res))
          .catch(console.error);
      }
    };

    socket.on('check:update', onUpdate);

    return () => {
      //console.log(`🌐 Unsubscribing WS from receiptId=${receiptId}`);
      socket.emit('check:unsubscribe', { receiptId });
      socket.off('check:update', onUpdate);
    };
  }, [socket, receiptId, receipt.status, updateReceipt]);

  // 3) Логируем изменение selectedReceipt из стора
  useEffect(() => {
    //console.log('>>> store.selectedReceipt now:', receipt);
  }, [receipt]);

  if (loadingCheck) {
    return <div className={styles.container}>Загрузка статуса чека…</div>;
  }
  if (!checkData) {
    return <div className={styles.container}>Ожидаем пробития чека…</div>;
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.heading}>Статус чека: {status}</h3>

      <ul className={styles.list}>
        <li className={styles.listItem}>
          <strong>UUID:</strong> {checkData.atol_uuid || '-'}
        </li>
        <li className={styles.listItem}>
          <strong>Смена:</strong> {checkData.shift_number || '-'}
        </li>
        <li className={styles.listItem}>
          <strong>Контакты:</strong>{' '}
          {checkData.sendOptions?.email || '-'} /{' '}
          {checkData.sendOptions?.phone || '-'}
        </li>
      </ul>

      {checkData.products && (
        <div className={styles.productList}>
          <h4 className={styles.subheading}>Товары с маркировкой</h4>
          {checkData.products.map(prod => (
            <div key={prod.product_number} className={styles.productItem}>
              <span className={styles.productName}>{prod.product_name}</span>
              <span className={styles.marks}>
                {prod.marks && prod.marks.length > 0
                  ? prod.marks.join(', ')
                  : 'нет'}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.actions}>
        {checkData.check_link && (
          <>
            <button
              className={styles.button}
              onClick={() => copyLink(checkData.check_link)}
            >
              Скопировать ссылку
            </button>
            <button
              className={styles.button}
              onClick={() => window.open(checkData.check_link, '_blank')}
            >
              Посмотреть чек
            </button>
          </>
        )}
        {receipt?.status === 'Открыта' && (
          <button
            className={styles.button}
            onClick={closeReceipt}
            disabled={!showManualClose}
          >
            {showManualClose
              ? 'Закрыть без ожидания чека'
              : `Закрыть без ожидания чека (${remaining}s)`}
          </button>
        )}
      </div>
    </div>
  );
}
